<template>
  <div class="settings-tool-component">
    <ToolTitle :title="title" />
    <div id="settings-wrap">
      <div class="settings">
        <div @click="handleClick($event, 'guidelines')">
          Show Guidelines
        </div>
        <div class="settings-input" id="guidelines" @click="handleClick($event, 'guidelines')">
          <div class="checked">
            <img src="@/assets/settings-checked.svg" />
          </div>
          <div class="unchecked">
            <img src="@/assets/settings-unchecked.svg" />
          </div>
          <div class="slide" ref="guidelinesSlide"></div>
          <input type="checkbox" ref="guidelinesInput" hidden />
        </div>
      </div>
      <div class="settings">
        <div @click="handleClick($event, 'rulers')">Show Rulers</div>
        <div class="settings-input" id="rulers" @click="handleClick($event, 'rulers')">
          <div class="checked">
            <img src="@/assets/settings-checked.svg" />
          </div>
          <div class="unchecked">
            <img src="@/assets/settings-unchecked.svg" />
          </div>
          <div class="slide" ref="rulersSlide"></div>
          <input type="checkbox" ref="rulersInput" hidden />
        </div>
      </div>
      <div class="settings">
        <div @click="handleClick($event, 'grid')">Snap to Grid</div>
        <div class="settings-input" id="grid" @click="handleClick($event, 'grid')">
          <div class="checked">
            <img src="@/assets/settings-checked.svg" />
          </div>
          <div class="unchecked">
            <img src="@/assets/settings-unchecked.svg" />
          </div>
          <div class="slide" ref="gridSlide"></div>
          <input type="checkbox" ref="gridInput" hidden />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from "vue";
import { store } from "@/store";

import { canvas } from "@/canvas-api/Canvas";
import { handleGridLines } from "@/canvas-api/GridLines";
import { handleGuidelines } from "@/canvas-api/GuideLines";

import ToolTitle from "@/components/SideToolBar/Tools/ToolTitle.vue";

import { ActionTypes } from "@/store/actions";
import { SettingsState } from "@/types";

export default defineComponent({
  name: "Settings",
  components: {
    ToolTitle,
  },
  setup() {
    const title = ref("");

    const settingsState = computed(() => store.state.settingsState);

    const guidelinesSlide = ref<HTMLDivElement>();
    const guidelinesInput = ref<HTMLInputElement>();

    const rulersSlide = ref<HTMLDivElement>();
    const rulersInput = ref<HTMLInputElement>();

    const gridSlide = ref<HTMLDivElement>();
    const gridInput = ref<HTMLInputElement>();

    const elements = {
      guidelines: {
        slider: guidelinesSlide,
        input: guidelinesInput,
      },
      rulers: {
        slider: rulersSlide,
        input: rulersInput,
      },
      grid: {
        slider: gridSlide,
        input: gridInput,
      },
    } as { [key: string]: any };

    onMounted(() => {
      for (let setting of Object.keys(settingsState.value)) {
        if (settingsState.value[setting as keyof SettingsState]) {
          if (elements[setting].slider.value && elements[setting].input.value) {
            const slide = elements[setting].slider.value;

            slide.style.left = "50%";
            slide.style.borderTopLeftRadius = "0px";
            slide.style.borderBottomLeftRadius = "0px";
            slide.style.borderTopRightRadius = "4px";
            slide.style.borderBottomRightRadius = "4px";

            elements[setting].input.value.checked = true;
          } else {
            throw new Error("Missing Element in handle click!, Settings.vue");
          }
        }
      }

      title.value = "Settings";
    });

    const handleClick = (e: { target: HTMLInputElement }, setting: string) => {
      if (elements[setting].slider.value && elements[setting].input.value) {
        if (elements[setting].input.value.checked) {
          const slide = elements[setting].slider.value;
          slide.style.left = "0";
          slide.style.borderTopLeftRadius = "4px";
          slide.style.borderBottomLeftRadius = "4px";
          slide.style.borderTopRightRadius = "0px";
          slide.style.borderBottomRightRadius = "0px";
        } else {
          const slide = elements[setting].slider.value;
          slide.style.left = "50%";
          slide.style.borderTopLeftRadius = "0px";
          slide.style.borderBottomLeftRadius = "0px";
          slide.style.borderTopRightRadius = "4px";
          slide.style.borderBottomRightRadius = "4px";
        }

        elements[setting].input.value.checked = !elements[setting].input.value.checked;
      } else {
        throw new Error("Missing Element in handle click!, Settings.vue");
      }

      const payload: string = setting;
      store.dispatch(ActionTypes.SET_SETTINGS_STATE, payload);
    };

    const guidelines = computed<boolean>(() => store.getters.settingsState.guidelines);
    const showGrid = computed<boolean>(() => store.getters.settingsState.grid);

    watch(guidelines, (newState: boolean) => {
      handleGuidelines(canvas.value, newState);
    });

    watch(showGrid, () => {
      handleGridLines(canvas.value);
    });

    return {
      title,
      guidelinesSlide,
      guidelinesInput,
      rulersSlide,
      rulersInput,
      gridSlide,
      gridInput,
      handleClick,
    };
  },
});
</script>

<style scoped>
#settings-wrap {
  width: 315px;
  margin: 20px auto 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.settings {
  width: 80%;
  height: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
}

.settings div {
  cursor: pointer;
}

.settings-input {
  width: 60px;
  height: 30px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.checked {
  background: #0096ff;
  width: 50%;
  height: 100%;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.unchecked {
  background: #e74c3c;
  width: 50%;
  height: 100%;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.checked,
.unchecked {
  display: flex;
  justify-content: center;
  align-items: center;
}

.checked img,
.unchecked img {
  width: 50%;
  height: 50%;
}

.slide {
  width: 50%;
  height: 100%;
  left: 0;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  position: absolute;
  background: #fff;
  transition: left 0.25s ease;
}
</style>
